import { useState } from "react";
import { Bookmark, Plus, Trash2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  addView,
  loadViews,
  persistViews,
  removeView,
  type SavedView,
} from "@/lib/saved-views";

/** The parts of a view that come from the current table state. */
export type SavedViewState = Omit<SavedView, "id" | "name">;

interface SavedViewsProps {
  current: SavedViewState;
  onApply: (view: SavedView) => void;
}

/**
 * Named snapshots of the table setup (grouping + metrics + filters + range),
 * saved per-browser. Lets a user flip between "Revenue by brand" and "GSC by
 * domain" without rebuilding the controls each time.
 */
export function SavedViews({ current, onApply }: SavedViewsProps) {
  // Load once on mount; this is the only reader/writer of the list.
  const [views, setViews] = useState<SavedView[]>(() => loadViews());
  const [name, setName] = useState("");

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const view: SavedView = {
      id:
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : String(Date.now()),
      name: trimmed,
      ...current,
    };
    setViews(persistViews(addView(views, view)));
    setName("");
  };

  const del = (id: string) => setViews(persistViews(removeView(views, id)));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2 cursor-pointer">
          <Bookmark className="h-4 w-4" />
          Views{views.length ? ` (${views.length})` : ""}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-2 p-2">
        {views.length === 0 ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">
            No saved views yet. Save the current setup below.
          </p>
        ) : (
          <div className="max-h-64 space-y-0.5 overflow-y-auto">
            {views.map((v) => (
              <div
                key={v.id}
                className="group flex items-center gap-1 rounded hover:bg-muted"
              >
                <button
                  type="button"
                  onClick={() => onApply(v)}
                  title="Apply this view"
                  className="flex-1 cursor-pointer truncate px-2 py-1.5 text-left text-sm"
                >
                  {v.name}
                </button>
                <button
                  type="button"
                  onClick={() => del(v.id)}
                  title="Delete this view"
                  className="shrink-0 cursor-pointer rounded p-1 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 border-t border-border pt-2">
          <Input
            className="h-8"
            placeholder="Name this view"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
            }}
          />
          <Button
            size="sm"
            className="h-8 shrink-0"
            onClick={save}
            disabled={!name.trim()}
          >
            <Plus className="size-3.5" /> Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
